/* eslint-disable @atlassian/tangerine/import/no-parent-imports */

import React, { useMemo, useRef, createRef } from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import FilterIcon from '@atlaskit/icon/core/filter';
import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Text, xcss, Flex } from '@atlaskit/primitives';

import { type FilterId } from '../../../../../common/constants/filters';
import {
	type PrimaryProductKey,
	PrimaryProductKeys,
	ProductKeys,
	useProductConfigs,
} from '../../../../../common/constants/products';
import { PRODUCTS_WITH_INLINE_FILTERS } from '../../../../../common/constants/quick-find';
import { SearchExperience } from '../../../../../common/constants/search-experience';
import { useOverflowObserver } from '../../../../../common/ui/overflow-observer';
import { FilterButton } from '../../../../../common/ui/quick-find/filter-button';
import {
	useQuickFindAnalytics,
	useQuickFindAttributes,
} from '../../../../../controllers/quick-find/analytics';
import {
	useFiltersDiscoverability2Enabled,
	usePrimaryProduct,
} from '../../../../../controllers/store/bootstrap';
import {
	useAnyFilterSelected,
	useFiltersToDisplay,
	useSearchActions,
	useSelectedProduct,
} from '../../../../../controllers/store/search';
import {
	assetsSchemaFilter,
	assetsTypeFilter,
	assigneeFilter,
	commentedByMeFilter,
	contributorFilter,
	hasAttachmentFilter,
	projectFilter,
	spaceFilter,
	watchedFilter,
} from '../../../../../controllers/store/search/filters';
import { filters } from '../../../../search-page-relay/filters/product-filters';
import DropdownFilter from '../../../../search-page-relay/filters/product-filters/dropdown';
import { AssetsSchemaFilter } from '../../../filters/product-filters/assets-schema';
import { AssetsTypeFilter } from '../../../filters/product-filters/assets-type';

import { AppSelector } from './app-selector';
import { i18n } from './messages';
import { useUnselectProductFilter } from './use-unselect-product-filter';
import { useFilterAnalytics } from './utils';

const newInlineProductFilterStyles = xcss({
	display: 'flex',
	gap: 'space.100',
	minWidth: '0', // Prevents the filter buttons from overflowing from its container and allow it to shrink as much as possible
	width: '100%',
	overflow: 'hidden',
});

const dynamicInlineFilters = xcss({
	flex: 1,
	minWidth: '0',
	overflow: 'hidden',
	gap: 'space.100',
});

const appSelectorDividerStyles = xcss({
	borderWidth: 'border.width',
	borderColor: 'color.border',
	borderRightStyle: 'solid',
	marginBlock: 'space.025',
});

const invisibleStyles = xcss({
	visibility: 'hidden',
});

type InlineProductFiltersProps = {
	onAllFiltersClickCallback?: (params: {
		action?: 'filterByPrimaryProduct';
		event: React.MouseEvent;
	}) => void;
};

const prioritisedFilters: Record<PrimaryProductKey, string[]> = {
	[PrimaryProductKeys.Confluence]: [spaceFilter.id, contributorFilter.id],
	[PrimaryProductKeys.Jira]: [projectFilter.id, assigneeFilter.id],
	// Not supported
	[PrimaryProductKeys.Atlas]: [],
	[PrimaryProductKeys.Focus]: [],
	[PrimaryProductKeys.Assets]: [assetsSchemaFilter.id, assetsTypeFilter.id],
	[PrimaryProductKeys.Elevate]: [],
	[PrimaryProductKeys.Talent]: [],
	[PrimaryProductKeys.RovoExtension]: [],
};

const QUICK_FIND_EXCLUDED_FILTER_IDS: readonly FilterId[] = [
	commentedByMeFilter.id,
	watchedFilter.id,
	hasAttachmentFilter.id,
];

const ClearAllButton = ({ onClear }: { onClear: VoidFunction }) => {
	const { formatMessage } = useIntl();
	return (
		<Box id="clear-all-button">
			<Button onClick={onClear} appearance="subtle">
				<Text>{formatMessage(i18n.clearFilters)}</Text>
			</Button>
		</Box>
	);
};

const VisibleFilterWrapper = React.forwardRef<
	HTMLDivElement,
	{
		visibleFilters: string[];
		id: string;
		children: React.ReactNode;
	}
>(({ id, visibleFilters, children }, ref) => {
	const elementId = `reference:${id}`;
	const isVisible = visibleFilters.includes(id);
	return (
		<Box id={elementId} ref={ref} xcss={isVisible ? undefined : invisibleStyles}>
			{children}
		</Box>
	);
});

export const InlineProductFilters = ({
	onAllFiltersClickCallback,
}: InlineProductFiltersProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const [primaryProduct] = usePrimaryProduct();
	const [selectedProduct] = useSelectedProduct();
	const productConfigs = useProductConfigs();
	const [isAnyFilterSelected] = useAnyFilterSelected();
	const filtersDiscoverability2Enabled = useFiltersDiscoverability2Enabled();
	const { clearAllFilters, resetSort } = useSearchActions();
	const [{ filtersInLine }] = useFiltersToDisplay({
		basedOnPrimaryProduct: true,
		primaryProduct,
		prioritisedFilterIds: primaryProduct ? prioritisedFilters[primaryProduct] : [],
	});
	const filtersToRender = useMemo(
		() =>
			filtersInLine
				.filter((f) => !(QUICK_FIND_EXCLUDED_FILTER_IDS as readonly string[]).includes(f))
				.slice(0, 2), // Only show 2 filters at a time
		[filtersInLine],
	);

	const filterRefs = useRef<React.MutableRefObject<HTMLDivElement>[]>([]);
	const containerRef = useRef<HTMLDivElement>();
	filterRefs.current = filtersToRender
		.filter((id) => filters[id as keyof typeof filters])
		.map((_, i) => filterRefs.current[i] ?? createRef());
	const visibleFilters = useOverflowObserver({
		containerRef,
		itemRefs: filterRefs,
		itemIds: filtersToRender,
	});

	const { fireAnalyticsEvent } = useQuickFindAnalytics();
	const { commonAttributes } = useQuickFindAttributes();
	const { onFilterChange, onDropdownOpenChange, onFilterClearAction } = useFilterAnalytics(
		fireAnalyticsEvent,
		commonAttributes,
		SearchExperience.QUICK_FIND,
	);
	useUnselectProductFilter({ isEnabled: false });

	/**
	 * Defines inline product filters to show for each primary product. Given that
	 * the filters we decide to show are determined by product and design, there
	 * isn't a need to make this dynamic yet.
	 */
	const inlineProductFilters = useMemo(() => {
		// rovo-extension uses Confluence's config since it's not a searchable product
		const productConfigKey =
			primaryProduct === PrimaryProductKeys.RovoExtension && fg('quickfind-rovo-extension')
				? ProductKeys.Confluence
				: (primaryProduct as ProductKeys);
		const primaryProductDisplayName = productConfigs[productConfigKey]?.displayName ?? '';
		const moreFiltersButton = (
			<FilterButton
				onClick={(event) => {
					onAllFiltersClickCallback?.({
						...(filtersDiscoverability2Enabled && {
							filterDropdownOpen: true,
						}),
						action: 'filterByPrimaryProduct',
						event,
					});
				}}
				iconAfter={<FilterIcon label={formatMessage(i18n.filterByIcon)} />}
				noBorder={true}
				tooltip={formatMessage(i18n.moreButtonTooltip, { product: primaryProductDisplayName })}
				testId="quick-find-inline-product-filters-more-filters-button"
			/>
		);

		if (
			primaryProduct === PrimaryProductKeys.Confluence ||
			primaryProduct === PrimaryProductKeys.Jira ||
			(primaryProduct === PrimaryProductKeys.RovoExtension && fg('quickfind-rovo-extension'))
		) {
			return (
				<Box xcss={newInlineProductFilterStyles} testId="qf-product-filters">
					<DropdownFilter
						hideOnHoverOut={
							primaryProduct === PrimaryProductKeys.Jira &&
							fg('search_qf_close_dropdown_on_hover_off')
						}
						experience={SearchExperience.QUICK_FIND}
						onFiltersChange={onFilterChange}
						onDropdownOpenChange={onDropdownOpenChange}
					/>
					<AppSelector fit="compact" />
					<Box
						xcss={[
							appSelectorDividerStyles,
							// Do not show the divider if there is no inline filters or clear button
							visibleFilters.length === 0 ? invisibleStyles : undefined,
						]}
					/>
					<Flex xcss={dynamicInlineFilters} ref={containerRef}>
						{filtersToRender.map((id, index) => {
							const Filter = filters[id as keyof typeof filters];
							if (!Filter) {
								return null;
							}
							return (
								<VisibleFilterWrapper
									id={id}
									ref={filterRefs.current[index]}
									visibleFilters={visibleFilters}
									key={id}
								>
									<Filter
										showPrimaryProductIcon={!selectedProduct}
										fit="compact"
										hideOnHoverOut={
											primaryProduct === PrimaryProductKeys.Jira &&
											fg('search_qf_close_dropdown_on_hover_off')
										}
										onFilterChange={(change) => onFilterChange(id, change, 'inline')}
									/>
								</VisibleFilterWrapper>
							);
						})}
					</Flex>
					{isAnyFilterSelected && (
						<ClearAllButton
							onClear={() => {
								clearAllFilters();
								resetSort();
								onFilterClearAction('inline');
							}}
						/>
					)}
				</Box>
			);
		} else if (primaryProduct === PrimaryProductKeys.Assets && fg('assets_rovo_search_all')) {
			return (
				<Box xcss={newInlineProductFilterStyles} testId="qf-product-filters">
					<AssetsSchemaFilter />
					<AssetsTypeFilter />
					{moreFiltersButton}
				</Box>
			);
		} else {
			return null;
		}
	}, [
		clearAllFilters,
		filtersDiscoverability2Enabled,
		filtersToRender,
		formatMessage,
		isAnyFilterSelected,
		onAllFiltersClickCallback,
		onDropdownOpenChange,
		onFilterChange,
		onFilterClearAction,
		primaryProduct,
		productConfigs,
		resetSort,
		selectedProduct,
		visibleFilters,
	]);

	if (PRODUCTS_WITH_INLINE_FILTERS.includes(primaryProduct)) {
		return inlineProductFilters;
	}

	if (selectedProduct) {
		return null;
	}

	return null;
};
